import React, { useState, useContext } from "react";
import "./teamModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import { User2, Trash2 } from "lucide-react";
import translations from "../../../translations/translations";
import BannerMessage from "../../common/bannerMessage/bannerMessage";

const TeamModal = ({
    onClose,
    shopData,
    onStaffAdded,
    onStaffRemoved,
    onRoleUpdated,
}) => {
    const { userData, language } = useContext(CombinedContext);
    const team = shopData.staffs || [];
    const [adding, setAdding] = useState(false);
    const [newStaff, setNewStaff] = useState([{ firstName: "", lastName: "", email: "" }]);
    const [loading, setLoading] = useState(false);

    // Local popup state
    const [confirmPopup, setConfirmPopup] = useState({ open: false, member: null });
    const [banner, setBanner] = useState(null);
    const [cancelPopup, setCancelPopup] = useState({ open: false, member: null });
    const [cancelLoading, setCancelLoading] = useState(false);

    const isOwner = userData?.user?.role === "owner";
    const canManageRoles = isOwner && shopData?.setup !== true;

    const showBanner = (type, text) => {
        setBanner({ type, text });

        setTimeout(() => {
            setBanner(null);
        }, 3000);
    };

    const addNewRow = () => setNewStaff((prev) => [...prev, { firstName: "", lastName: "", email: "" }]);
    const removeRow = (index) => setNewStaff((prev) => prev.filter((_, i) => i !== index));

    const updateRow = (index, field, value) => {
        setNewStaff((prev) => {
            const updated = [...prev];
            updated[index][field] = value;
            return updated;
        });
    };

    // ───────────────────────────────────────────────
    // ✅ Add new staff invites
    // ───────────────────────────────────────────────
    const handleConfirmAdd = async () => {
        // 🟢 If not adding, just close
        if (!adding) {
            onClose();
            return;
        }
    
        if (!userData?.user?.shopId) return;
    
        setLoading(true);
    
        try {
            const validStaff = newStaff.filter((s) => s.email.trim());
    
            if (validStaff.length === 0) {
                showBanner("error", "Please enter at least one valid email.");
                setLoading(false);
                return;
            }
    
            const emails = validStaff.map((s) => s.email.trim());
            const firstNames = validStaff.map((s) => s.firstName.trim() || "");
            const lastNames = validStaff.map((s) => s.lastName.trim() || "");
    
            const res = await companyBasedRequest.invitestaff(
                userData.user.shopId,
                emails,
                firstNames,
                lastNames,
                userData.cognitoTokens.AccessToken
            );
    
            onStaffAdded?.(res.newStaff);
            setNewStaff([{ firstName: "", lastName: "", email: "" }]);
            setAdding(false);
    
            onClose(); // 🟢 close after success
        } catch (err) {
            console.error(err);
            showBanner("error", "Failed to send some invites.");
        } finally {
            setLoading(false);
        }
    };
    

    const handlePromote = async (member) => {
        try {
            await companyBasedRequest.promoteStaff(
                userData.user.shopId,
                member.userId,
                userData.user.userId,
                userData.cognitoTokens.AccessToken
            );

            onRoleUpdated?.(member.userId, "admin");
            showBanner(
                "success",
                translations[language].teamPage.toast.promotedToAdmin.replace("{{name}}", member.firstName)
            );
        } catch (err) {
            console.error(err);
            showBanner("error", "Failed to promote.");
        }
    };

    const handleDemote = async (member) => {
        try {
            await companyBasedRequest.demoteStaff(
                userData.user.shopId,
                member.userId,
                userData.user.userId,
                userData.cognitoTokens.AccessToken
            );

            onRoleUpdated?.(member.userId, "staff");            showBanner(
                "success",
                translations[language].teamPage.toast.demotedToStaff.replace("{{name}}", member.firstName)
            );
        } catch (err) {
            console.error(err);
            showBanner("error", "Failed to demote.");
        }
    };

    const handleReinvite = async (member) => {
        try {
            await companyBasedRequest.reinviteStaff(shopData.shopId, member.email, userData.cognitoTokens.AccessToken);

            showBanner("success", translations[language].teamPage.toast.inviteResent);
        } catch (err) {
            console.error(err);
            showBanner("error", translations[language].teamPage.messages.reinviteFailed);
        }
    };

    // Open cancel invite popup
    const handleCancelInvite = (member) => {
        if (member.role === "owner") return;
        setCancelPopup({ open: true, member });
    };

    // Confirm cancel invite
    const confirmCancelInvite = async () => {
        if (!userData?.user?.shopId || !cancelPopup.member) return;
        setCancelLoading(true);

        try {
            await companyBasedRequest.cancelInvite(
                shopData.shopId,
                cancelPopup.member.email,
                userData.cognitoTokens.AccessToken
            );

            // Remove from UI
            onStaffRemoved?.(cancelPopup.member.email);

            showBanner(
                "success",
                translations[language].teamPage.toast.inviteCancelled.replace(
                    "{{name}}",
                    cancelPopup.member.firstName || cancelPopup.member.email
                )
            );
        } catch (err) {
            console.error(err);
            showBanner("error", "Failed to cancel invite. Please try again.");
        } finally {
            setCancelLoading(false);
            setCancelPopup({ open: false, member: null });
        }
    };

    const handleRemoveStaff = (member) => {
        if (member.role === "owner") return;
        setConfirmPopup({ open: true, member });
    };

    const confirmRemove = async () => {
        if (!userData?.user?.shopId || !confirmPopup.member) return;

        try {
            await companyBasedRequest.removeStaff(
                userData.user.shopId,
                confirmPopup.member.userId,
                confirmPopup.member.email,
                userData.cognitoTokens.AccessToken
            );
            onStaffRemoved?.(confirmPopup.member.email);
            showBanner("success", `${confirmPopup.member.firstName || "Staff"} removed successfully.`);
        } catch (err) {
            console.error(err);
            showBanner("error", "Failed to remove staff. Please try again.");
        } finally {
            setConfirmPopup({ open: false, member: null });
        }
    };

    return (
        <div className="sf-team-overlay" onClick={onClose}>
            <div className="sf-team-modal" onClick={(e) => e.stopPropagation()}>
                {/* HEADER */}
                <div className="sf-team-header">
                    <h2 className="sf-team-title">{translations[language].teamPage.header.title}</h2>
                    <button className="sf-team-close" onClick={onClose}>
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="sf-team-body">
                    {team.map((m, i) => (
                        <div className="sf-team-block" key={i}>
                            <div className="sf-team-item">
                                {/* AVATAR */}
                                {m.image ? (
                                    <img className="sf-team-avatar" src={m.image} alt={m.firstName || "Staff"} />
                                ) : (
                                    <div className="sf-team-avatar-fallback">
                                        <span>{m.firstName?.charAt(0).toUpperCase()}</span>
                                    </div>
                                )}

                                {/* INFO */}
                                <div className="sf-team-info">
                                    <div className="sf-team-name-row">
                                        <div className="sf-team-name">
                                            {m.firstName} {m.lastName}
                                        </div>

                                        {/* ROLE BADGE */}
                                        <span className={`sf-team-role-badge role-${m.role}`}>
                                            <span className={`sf-team-role-badge role-${m.role}`}>
                                                {translations[language].teamPage.role[m.role] ||
                                                    translations[language].teamPage.status[m.status] ||
                                                    m.role}
                                            </span>
                                        </span>
                                    </div>

                                    <div className="sf-team-sub">{m.email}</div>

                                    {/* ACTION BUTTONS */}
                                    <div className="sf-team-actions-row">
                                        {/* OWNER-ONLY: Promote / Demote */}
                                        {canManageRoles && (
                                            <>
                                                {m.role !== "admin" && m.status === "active" && (
                                                    <button
                                                        className="sf-team-action-btn promote"
                                                        onClick={() => handlePromote(m)}
                                                    >
                                                        {translations[language].teamPage.actions.promoteToAdmin}
                                                    </button>
                                                )}

                                                {m.role === "admin" && (
                                                    <button
                                                        className="sf-team-action-btn demote"
                                                        onClick={() => handleDemote(m)}
                                                    >
                                                        {translations[language].teamPage.actions.demoteToStaff}
                                                    </button>
                                                )}
                                            </>
                                        )}

                                        {/* Re-invite (anyone can see) */}
                                        {m.status === "invited" && (
                                            <button
                                                className="sf-team-action-btn reinvite"
                                                onClick={() => handleReinvite(m)}
                                            >
                                                {translations[language].teamPage.actions.reinvite}
                                            </button>
                                        )}

                                        {/* Delete (owner-only) */}
                                        {isOwner && m.role !== "owner" && (
                                            <>
                                                {m.status === "invited" && !m.joined ? (
                                                    <button
                                                        className="sf-team-action-btn cancel"
                                                        onClick={() => handleCancelInvite(m)}
                                                    >
                                                        {translations[language].teamPage.actions.cancelInvite}
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="sf-team-remove"
                                                        onClick={() => handleRemoveStaff(m)}
                                                    >
                                                        <Trash2 size={18} strokeWidth={1.8} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Adding new staff */}
                    {adding &&
                        newStaff.map((s, i) => (
                            <div className="sf-team-block" key={`new-${i}`}>
                                <div className="sf-team-item">
                                    <div className="sf-team-avatar-fallback">
                                        <User2 size={26} strokeWidth={1.6} />
                                    </div>

                                    <div className="sf-team-info">
                                        <div className="sf-team-input-names">
                                            <input
                                                className="sf-team-input"
                                                type="text"
                                                placeholder={translations[language].teamPage.labels.firstName}
                                                value={s.firstName}
                                                onChange={(e) => updateRow(i, "firstName", e.target.value)}
                                            />
                                            <input
                                                className="sf-team-input"
                                                type="text"
                                                placeholder={translations[language].teamPage.labels.lastName}
                                                value={s.lastName}
                                                onChange={(e) => updateRow(i, "lastName", e.target.value)}
                                            />
                                        </div>
                                        <input
                                            className="sf-team-input"
                                            type="email"
                                            placeholder={translations[language].teamPage.labels.emailAddress}
                                            value={s.email}
                                            onChange={(e) => updateRow(i, "email", e.target.value)}
                                        />
                                    </div>

                                    <button
                                        className="sf-team-remove"
                                        onClick={() => removeRow(i)}
                                        disabled={newStaff.length === 1}
                                    >
                                        <Trash2 size={18} strokeWidth={1.8} />
                                    </button>
                                </div>
                            </div>
                        ))}

                    {/* Add button */}
                    <button
                        className="sf-team-addbtn"
                        onClick={() => {
                            if (adding) addNewRow();
                            else setAdding(true);
                        }}
                    >
                        {translations[language].teamPage.actions.addStaff}
                    </button>
                </div>

                {banner && <BannerMessage type={banner.type} text={banner.text} />}

                {/* FOOTER */}
                <div className="sf-base-footer">
                    <button className="btn ghost" onClick={onClose} disabled={loading}>
                        {translations[language].teamPage.actions.cancel ||
                            translations[language].calendar.editor.cancel}
                    </button>

                    <button className="btn" onClick={handleConfirmAdd} disabled={loading}>
                        {loading
                            ? translations[language].teamPage.actions.sending
                            : translations[language].calendar.editor.save}
                    </button>
                </div>

                {/* Confirm Remove Popup */}
                {confirmPopup.open && (
                    <div className="sf-confirm-overlay">
                        <div className="sf-confirm-box">
                            <p>
                                {translations[language].teamPage.confirm.popupTitle.replace(
                                    "{{name}}",
                                    confirmPopup.member.firstName || confirmPopup.member.email
                                )}
                                <br />
                                {translations[language].teamPage.confirm.popupSubtitle}
                            </p>
                            <div className="sf-confirm-actions">
                                <button
                                    className="btn ghost"
                                    onClick={() => setConfirmPopup({ open: false, member: null })}
                                >
                                    {translations[language].teamPage.confirm.cancel}
                                </button>
                                <button className="btn danger" onClick={confirmRemove}>
                                    {translations[language].teamPage.confirm.confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Cancel Invite Popup */}
                {cancelPopup.open && (
                    <div className="sf-confirm-overlay">
                        <div className="sf-confirm-box">
                            <p>
                                {translations[language].teamPage.confirm.cancelInviteTitle.replace(
                                    "{{name}}",
                                    cancelPopup.member.firstName || cancelPopup.member.email
                                )}
                                <br />
                                {translations[language].teamPage.confirm.cancelInviteSubtitle}
                            </p>
                            <div className="sf-confirm-actions">
                                <button
                                    className="btn ghost"
                                    onClick={() => setCancelPopup({ open: false, member: null })}
                                    disabled={cancelLoading}
                                >
                                    {translations[language].teamPage.actions.cancel}
                                </button>
                                <button className="btn danger" onClick={confirmCancelInvite} disabled={cancelLoading}>
                                    {cancelLoading
                                        ? translations[language].teamPage.actions.removing
                                        : translations[language].teamPage.actions.confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamModal;
